import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, Field, NumberField, PageHeader, StatusPill, Toggle, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useToast } from "@ozpanel/ui";
import { useAccountsApi, type FeatureField, type LimitField, type Plan } from "../../accountsApi";

// Plans — what an account is allowed to do, and the only place it can be decided.
//
// v2 could READ plans and nothing else: TenantsScreen calls listPlans() to fill a dropdown, and
// POST/PUT/DELETE /plans were reached by no console at all. So a plan could be assigned and never
// created or corrected — the limits every account inherits were fixed at whatever the seed data
// happened to say, editable only by calling the API by hand or writing SQL.
//
// The form is rendered from the SERVER's catalogues, not from a list written here. GET /plans
// returns `fields` (every limit the platform knows, with its label, group, unit and an explanation
// the server wrote) and `features` (every capability). A limit added server-side therefore appears
// in this form with no frontend change, and one that is removed stops being offered rather than
// silently writing a key nothing reads — the same reason SettingsField.OptionsFrom exists.

export function PlansScreen() {
  const api = useAccountsApi();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({ queryKey: ["plans"], queryFn: api.listPlans, retry: false });
  const [editing, setEditing] = React.useState<Plan | null>(null);
  const [deleting, setDeleting] = React.useState<Plan | null>(null);

  const fields = q.data?.fields ?? [];
  const features = q.data?.features ?? [];

  const cols: DataListColumn<Plan>[] = [
    { key: "name", label: "Plan", sortable: true, render: (p) => <span className="font-medium">{p.name}</span> },
    {
      key: "description",
      label: "What it is",
      render: (p) => <span className="text-[12px] text-muted">{p.description || "—"}</span>,
    },
    {
      key: "tenants",
      label: "Accounts on it",
      sortable: true,
      value: (p) => p.tenants ?? 0,
      render: (p) => <span>{p.tenants ?? 0}</span>,
    },
    {
      key: "visible",
      label: "Offered",
      render: (p) => (
        <StatusPill tone={p.visible ? "ok" : "neutral"}>{p.visible ? "visible" : "hidden"}</StatusPill>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Plans" subtitle="The limits and capabilities every account on a plan inherits" />
      <Section title="Plans" hint={q.data ? `${q.data.items?.length ?? 0}` : undefined} bodyClassName="p-0">
        <StateView loading={q.isLoading} error={q.error} errorContext="plans">
          <div className="p-4">
            <div className="mb-3">
              <Button
                size="sm"
                onClick={() =>
                  setEditing({ id: 0, name: "", description: "", limits: {}, features: {}, visible: true })
                }
              >
                New plan
              </Button>
            </div>
            <DataList
              items={q.data?.items ?? []}
              columns={cols}
              getRowKey={(p) => String(p.id)}
              emptyMessage="No plans. Accounts without a plan fall back to the platform defaults."
              rowActions={(p) => (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                    edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setDeleting(p)}>
                    delete
                  </Button>
                </>
              )}
            />
          </div>
        </StateView>
      </Section>

      {editing && (
        <PlanEditor
          plan={editing}
          fields={fields}
          features={features}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["plans"] });
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={deleting ? `Delete the ${deleting.name} plan?` : ""}
        confirmLabel="Delete it"
        destructive
        body={
          <p>
            Accounts already on this plan keep the limits they have; nothing is taken away from a
            running site. The server refuses the deletion outright while any account is still on
            it, so the count above is a warning and its answer is the authority.
          </p>
        }
        onConfirm={async () => {
          const p = deleting;
          setDeleting(null);
          if (!p) return;
          try {
            await api.deletePlan(p.id);
            notify({ kind: "success", title: `${p.name} deleted` });
            void qc.invalidateQueries({ queryKey: ["plans"] });
          } catch (e) {
            notify({ kind: "error", title: "Could not delete it", body: e instanceof Error ? e.message : String(e) });
          }
        }}
      />
    </>
  );
}

/** Create or edit one plan. Limits are grouped exactly as the server groups them. */
function PlanEditor({
  plan,
  fields,
  features,
  onClose,
  onSaved,
}: {
  plan: Plan;
  fields: LimitField[];
  features: FeatureField[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAccountsApi();
  const { notify } = useToast();
  const [name, setName] = React.useState(plan.name);
  const [description, setDescription] = React.useState(plan.description ?? "");
  const [visible, setVisible] = React.useState(plan.visible ?? true);
  // Kept as STRINGS: "" is a real value here and means "no limit", which is not 0. Coercing as
  // the operator types would turn an empty box into a hard cap of zero — the difference between
  // unlimited disk and a site that can write nothing.
  const [limits, setLimits] = React.useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(plan.limits ?? {})) out[k] = String(v);
    return out;
  });
  const [feats, setFeats] = React.useState<Record<string, boolean>>(plan.features ?? {});
  const [busy, setBusy] = React.useState(false);

  const groups = Array.from(new Set(fields.map((f) => f.group || "Other")));

  const save = async () => {
    if (!name.trim()) {
      notify({ kind: "error", title: "A plan needs a name" });
      return;
    }
    setBusy(true);
    try {
      const numeric: Record<string, number> = {};
      for (const [k, v] of Object.entries(limits)) {
        const t = v.trim();
        if (t === "") continue; // absent = no limit, which is not the same as 0
        const n = Number(t);
        if (Number.isFinite(n)) numeric[k] = n;
      }
      const body = { name: name.trim(), description: description.trim(), limits: numeric, features: feats, visible };
      if (plan.id) await api.updatePlan(plan.id, body);
      else await api.createPlan(body);
      notify({ kind: "success", title: plan.id ? "Plan updated" : "Plan created" });
      onSaved();
    } catch (e) {
      notify({ kind: "error", title: "Could not save it", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={plan.id ? `Edit ${plan.name}` : "New plan"} className="mt-6">
      <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Name" value={name} onChange={setName} placeholder="Starter" />
          <Field label="What it is" value={description} onChange={setDescription} placeholder="For a single small site" />
        </div>
        <Toggle
          checked={visible}
          onChange={setVisible}
          label="Offer this plan"
          help="A hidden plan still applies to the accounts already on it; it just stops being offered for new ones."
        />

        {features.length > 0 && (
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted">Capabilities</div>
            {features.map((f) => (
              <Toggle
                key={f.key}
                checked={Boolean(feats[f.key])}
                onChange={(b) => setFeats({ ...feats, [f.key]: b })}
                label={f.label}
                help={f.detail}
              />
            ))}
          </div>
        )}

        {groups.map((g) => (
          <div key={g}>
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted">{g}</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {fields
                .filter((f) => (f.group || "Other") === g)
                .map((f) => (
                  <NumberField
                    key={f.key}
                    label={f.label}
                    unit={f.unit}
                    value={limits[f.key] ?? ""}
                    placeholder={f.unset || "no limit"}
                    onChange={(v) => setLimits({ ...limits, [f.key]: v })}
                    help={f.detail}
                  />
                ))}
            </div>
          </div>
        ))}

        <p className="max-w-3xl text-[11.5px] leading-relaxed text-muted">
          An empty box means no limit — which is not the same as 0, and 0 would stop the account
          doing that thing at all. Changes apply immediately to every account on the plan, except
          where an account has been given its own override.
        </p>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : plan.id ? "Save changes" : "Create plan"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Section>
  );
}
